import React, { useState, useEffect, useMemo } from 'react';
import { 
  FiBarChart, FiPieChart, FiTrendingUp, FiTable, FiSave, 
  FiPlay, FiDownload, FiShare2, FiSettings, FiFilter,
  FiPlus, FiTrash2, FiMove, FiEye, FiCode, FiRefreshCw
} from 'react-icons/fi';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface DataSource {
  id: string;
  name: string;
  table: string;
  joins?: Array<{
    table: string;
    type: 'inner' | 'left' | 'right';
    condition: string;
  }>;
}

interface ReportField {
  id: string;
  name: string;
  label: string;
  table: string;
  type: 'dimension' | 'measure';
  dataType: 'text' | 'number' | 'date' | 'boolean';
  aggregation?: 'sum' | 'count' | 'avg' | 'min' | 'max' | 'distinct';
  format?: string;
  position?: { x: number; y: number; width: number; height: number };
}

interface ReportFilter {
  id: string;
  fieldId: string;
  operator: 'equals' | 'contains' | 'gt' | 'lt' | 'between' | 'in' | 'not_null';
  value: any;
  label?: string;
}

interface ChartConfig {
  type: 'line' | 'bar' | 'pie' | 'doughnut' | 'area';
  xAxis?: string;
  yAxis?: string[];
  colors?: string[];
  options?: any;
}

interface ReportConfig {
  id?: string;
  name: string;
  description?: string;
  reportType: 'table' | 'chart' | 'dashboard' | 'pivot';
  dataSource: DataSource;
  fields: ReportField[];
  filters: ReportFilter[];
  groupBy?: string[];
  orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  chartConfig?: ChartConfig;
  layout: 'table' | 'chart' | 'dashboard';
  refreshRate?: number;
  isPublic: boolean;
  isScheduled: boolean;
}

const reportTypes = [
  { value: 'table', label: 'Table Report', icon: FiTable },
  { value: 'chart', label: 'Chart Report', icon: FiBarChart },
  { value: 'dashboard', label: 'Dashboard', icon: FiTrendingUp },
  { value: 'pivot', label: 'Pivot Table', icon: FiTable }
];

const chartTypes = [
  { value: 'line', label: 'Line Chart', icon: FiTrendingUp },
  { value: 'bar', label: 'Bar Chart', icon: FiBarChart },
  { value: 'pie', label: 'Pie Chart', icon: FiPieChart },
  { value: 'doughnut', label: 'Doughnut Chart', icon: FiPieChart }
];

const aggregations = [
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'distinct', label: 'Distinct Count' }
];

// Sample data sources for demo
const sampleDataSources: DataSource[] = [
  {
    id: 'sales',
    name: 'Sales Data',
    table: 'sales_orders',
    joins: [
      { table: 'customers', type: 'left', condition: 'sales_orders.customer_id = customers.id' },
      { table: 'products', type: 'left', condition: 'sales_order_items.product_id = products.id' }
    ]
  },
  {
    id: 'crm',
    name: 'CRM Data',
    table: 'leads',
    joins: [
      { table: 'users', type: 'left', condition: 'leads.assigned_to = users.id' }
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory Data',
    table: 'products',
    joins: [
      { table: 'categories', type: 'left', condition: 'products.category_id = categories.id' },
      { table: 'inventory', type: 'left', condition: 'products.id = inventory.product_id' }
    ]
  }
];

// Sample fields for demo
const sampleFields: { [key: string]: ReportField[] } = {
  sales: [
    { id: 'order_date', name: 'order_date', label: 'Order Date', table: 'sales_orders', type: 'dimension', dataType: 'date' },
    { id: 'customer_name', name: 'customer_name', label: 'Customer Name', table: 'customers', type: 'dimension', dataType: 'text' },
    { id: 'total_amount', name: 'total_amount', label: 'Total Amount', table: 'sales_orders', type: 'measure', dataType: 'number' },
    { id: 'status', name: 'status', label: 'Status', table: 'sales_orders', type: 'dimension', dataType: 'text' },
    { id: 'product_name', name: 'product_name', label: 'Product Name', table: 'products', type: 'dimension', dataType: 'text' }
  ],
  crm: [
    { id: 'lead_name', name: 'first_name', label: 'Lead Name', table: 'leads', type: 'dimension', dataType: 'text' },
    { id: 'lead_status', name: 'status', label: 'Lead Status', table: 'leads', type: 'dimension', dataType: 'text' },
    { id: 'created_date', name: 'created_at', label: 'Created Date', table: 'leads', type: 'dimension', dataType: 'date' },
    { id: 'assignee', name: 'first_name', label: 'Assignee', table: 'users', type: 'dimension', dataType: 'text' },
    { id: 'lead_count', name: 'id', label: 'Lead Count', table: 'leads', type: 'measure', dataType: 'number', aggregation: 'count' }
  ],
  inventory: [
    { id: 'product_name', name: 'item_name', label: 'Product Name', table: 'products', type: 'dimension', dataType: 'text' },
    { id: 'category', name: 'name', label: 'Category', table: 'categories', type: 'dimension', dataType: 'text' },
    { id: 'stock_quantity', name: 'quantity', label: 'Stock Quantity', table: 'inventory', type: 'measure', dataType: 'number' },
    { id: 'unit_price', name: 'standard_selling_rate', label: 'Unit Price', table: 'products', type: 'measure', dataType: 'number' }
  ]
};

export const ReportBuilder: React.FC = () => {
  const [report, setReport] = useState<ReportConfig>({
    name: '',
    description: '',
    reportType: 'table',
    dataSource: sampleDataSources[0],
    fields: [],
    filters: [],
    layout: 'table',
    isPublic: false,
    isScheduled: false
  });

  const [activeTab, setActiveTab] = useState<'design' | 'data' | 'preview'>('design');
  const [selectedFields, setSelectedFields] = useState<ReportField[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showFieldPanel, setShowFieldPanel] = useState(true);

  // Get available fields based on selected data source
  const availableFields = useMemo(() => {
    return sampleFields[report.dataSource.id] || [];
  }, [report.dataSource]);

  // Generate sample data for preview
  const generateSampleData = () => {
    if (selectedFields.length === 0) return [];

    const sampleSize = 50;
    const data = [];

    for (let i = 0; i < sampleSize; i++) {
      const row: any = {};
      selectedFields.forEach(field => {
        switch (field.dataType) {
          case 'text':
            row[field.name] = `Sample ${field.label} ${i + 1}`;
            break;
          case 'number':
            row[field.name] = Math.floor(Math.random() * 1000) + 100;
            break;
          case 'date':
            const date = new Date();
            date.setDate(date.getDate() - Math.floor(Math.random() * 365));
            row[field.name] = date.toISOString().split('T')[0];
            break;
          case 'boolean':
            row[field.name] = Math.random() > 0.5;
            break;
        }
      });
      data.push(row);
    }

    return data;
  };

  // Update preview data when fields change
  useEffect(() => {
    setPreviewData(generateSampleData());
  }, [selectedFields]);

  const addField = (field: ReportField) => {
    if (!selectedFields.find(f => f.id === field.id)) {
      setSelectedFields(prev => [...prev, field]);
      setReport(prev => ({
        ...prev,
        fields: [...prev.fields, field]
      }));
    }
  };

  const removeField = (fieldId: string) => {
    setSelectedFields(prev => prev.filter(f => f.id !== fieldId));
    setReport(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const updateField = (fieldId: string, updates: Partial<ReportField>) => {
    setSelectedFields(prev => prev.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
    setReport(prev => ({
      ...prev,
      fields: prev.fields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  };

  const addFilter = () => {
    if (selectedFields.length === 0) return;

    const newFilter: ReportFilter = {
      id: `filter_${Date.now()}`,
      fieldId: selectedFields[0].id,
      operator: 'equals',
      value: ''
    };

    setReport(prev => ({
      ...prev,
      filters: [...prev.filters, newFilter]
    }));
  };

  const updateFilter = (filterId: string, updates: Partial<ReportFilter>) => {
    setReport(prev => ({
      ...prev,
      filters: prev.filters.map(f => 
        f.id === filterId ? { ...f, ...updates } : f
      )
    }));
  };

  const deleteFilter = (filterId: string) => {
    setReport(prev => ({
      ...prev,
      filters: prev.filters.filter(f => f.id !== filterId)
    }));
  };

  const executeReport = async () => {
    setIsExecuting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setPreviewData(generateSampleData());
    } catch (error) {
      console.error('Error executing report:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const saveReport = async () => {
    try {
      // API call to save report
      console.log('Saving report:', report);
      alert('Report saved successfully!');
    } catch (error) {
      console.error('Error saving report:', error);
      alert('Error saving report');
    }
  };

  const exportReport = () => {
    if (previewData.length === 0) return;

    const headers = selectedFields.map(f => f.label);
    const rows = previewData.map(row =>
      selectedFields.map(field => row[field.name])
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.name.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Generate chart data
  const chartData = useMemo(() => {
    if (report.layout !== 'chart' || previewData.length === 0) return null;

    const dimensions = selectedFields.filter(f => f.type === 'dimension');
    const measures = selectedFields.filter(f => f.type === 'measure');

    if (dimensions.length === 0 || measures.length === 0) return null;

    const xField = dimensions[0];
    const yField = measures[0];

    // Group data by x-axis
    const groups: { [key: string]: number } = {};
    previewData.forEach(row => {
      const xValue = row[xField.name];
      const yValue = Number(row[yField.name]) || 0;
      groups[xValue] = (groups[xValue] || 0) + yValue;
    });

    return {
      labels: Object.keys(groups),
      datasets: [{
        label: yField.label,
        data: Object.values(groups),
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1
      }]
    };
  }, [previewData, selectedFields, report.layout]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Field Panel */}
      {showFieldPanel && (
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Report Builder</h3>
            <p className="text-sm text-gray-600">Drag fields to create your report</p>
          </div>

          {/* Data Source Selection */}
          <div className="p-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Data Source</h4>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={report.dataSource.id}
              onChange={(e) => {
                const dataSource = sampleDataSources.find(ds => ds.id === e.target.value);
                if (dataSource) {
                  setReport(prev => ({ ...prev, dataSource }));
                  setSelectedFields([]);
                }
              }}
            >
              {sampleDataSources.map(ds => (
                <option key={ds.id} value={ds.id}>{ds.name}</option>
              ))}
            </select>
          </div>

          {/* Report Settings */}
          <div className="p-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Report Settings</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Report Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={report.name}
                  onChange={(e) => setReport(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter report name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Report Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={report.reportType}
                  onChange={(e) => setReport(prev => ({ ...prev, reportType: e.target.value as any }))}
                >
                  {reportTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Layout
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={report.layout}
                  onChange={(e) => setReport(prev => ({ ...prev, layout: e.target.value as any }))}
                >
                  <option value="table">Table</option>
                  <option value="chart">Chart</option>
                  <option value="dashboard">Dashboard</option>
                </select>
              </div>

              {report.layout === 'chart' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chart Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={report.chartConfig?.type || 'bar'}
                    onChange={(e) => setReport(prev => ({
                      ...prev,
                      chartConfig: { ...prev.chartConfig, type: e.target.value as any }
                    }))}
                  >
                    {chartTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Available Fields */}
          <div className="p-4">
            <h4 className="font-medium text-gray-900 mb-3">Available Fields</h4>
            <div className="space-y-2">
              {availableFields.map(field => (
                <button
                  key={field.id}
                  className="w-full px-3 py-2 text-left text-sm bg-gray-50 hover:bg-gray-100 
                           border border-gray-200 rounded-md transition-colors"
                  onClick={() => addField(field)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{field.label}</div>
                      <div className="text-xs text-gray-500 capitalize">
                        {field.type} • {field.dataType}
                      </div>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${
                      field.type === 'dimension' ? 'bg-blue-400' : 'bg-green-400'
                    }`} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFieldPanel(!showFieldPanel)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <FiFilter className="w-5 h-5" />
              </button>
              
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['design', 'data', 'preview'] as const).map(tab => (
                  <button
                    key={tab}
                    className={`px-3 py-1 rounded-md text-sm font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={executeReport}
                disabled={isExecuting || selectedFields.length === 0}
                className="px-3 py-2 text-sm bg-green-600 hover:bg-green-700 disabled:bg-gray-400
                         text-white rounded-md transition-colors flex items-center"
              >
                {isExecuting ? (
                  <FiRefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FiPlay className="w-4 h-4 mr-2" />
                )}
                Run Report
              </button>
              <button
                onClick={exportReport}
                disabled={previewData.length === 0}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50
                         text-gray-700 rounded-md transition-colors flex items-center"
              >
                <FiDownload className="w-4 h-4 mr-2" />
                Export
              </button>
              <button
                onClick={saveReport}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 
                         text-white rounded-md transition-colors flex items-center"
              >
                <FiSave className="w-4 h-4 mr-2" />
                Save Report
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'design' && (
            <ReportDesigner
              report={report}
              selectedFields={selectedFields}
              onRemoveField={removeField}
              onUpdateField={updateField}
              onAddFilter={addFilter}
              onUpdateFilter={updateFilter}
              onDeleteFilter={deleteFilter}
            />
          )}
          {activeTab === 'data' && (
            <DataQueryBuilder report={report} onUpdateReport={setReport} />
          )}
          {activeTab === 'preview' && (
            <ReportPreview
              report={report}
              data={previewData}
              chartData={chartData}
              isLoading={isExecuting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Report Designer Component
const ReportDesigner: React.FC<{
  report: ReportConfig;
  selectedFields: ReportField[];
  onRemoveField: (fieldId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<ReportField>) => void;
  onAddFilter: () => void;
  onUpdateFilter: (filterId: string, updates: Partial<ReportFilter>) => void;
  onDeleteFilter: (filterId: string) => void;
}> = ({ report, selectedFields, onRemoveField, onUpdateField, onAddFilter, onUpdateFilter, onDeleteFilter }) => {
  return (
    <div className="h-full p-6 overflow-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Selected Fields */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Selected Fields</h3>
            <span className="text-sm text-gray-500">{selectedFields.length} fields</span>
          </div>
          
          {selectedFields.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiTable className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No fields selected. Choose fields from the left panel to build your report.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedFields.map(field => (
                <FieldCard
                  key={field.id}
                  field={field}
                  onRemove={() => onRemoveField(field.id)}
                  onUpdate={(updates) => onUpdateField(field.id, updates)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button
              onClick={onAddFilter}
              disabled={selectedFields.length === 0}
              className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400
                       text-white rounded-md transition-colors flex items-center"
            >
              <FiPlus className="w-4 h-4 mr-1" />
              Add Filter
            </button>
          </div>
          
          {report.filters.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p className="text-sm">No filters applied. Add filters to refine your report data.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {report.filters.map(filter => (
                <FilterCard
                  key={filter.id}
                  filter={filter}
                  selectedFields={selectedFields}
                  onUpdate={(updates) => onUpdateFilter(filter.id, updates)}
                  onDelete={() => onDeleteFilter(filter.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Field Card Component
const FieldCard: React.FC<{
  field: ReportField;
  onRemove: () => void;
  onUpdate: (updates: Partial<ReportField>) => void;
}> = ({ field, onRemove, onUpdate }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${
            field.type === 'dimension' ? 'bg-blue-400' : 'bg-green-400'
          }`} />
          <span className="font-medium text-sm">{field.label}</span>
        </div>
        <button
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Label</label>
          <input
            type="text"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            value={field.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
          />
        </div>
        
        {field.type === 'measure' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Aggregation</label>
            <select
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              value={field.aggregation || 'sum'}
              onChange={(e) => onUpdate({ aggregation: e.target.value as any })}
            >
              {aggregations.map(agg => (
                <option key={agg.value} value={agg.value}>{agg.label}</option>
              ))}
            </select>
          </div>
        )}
        
        <div className="text-xs text-gray-500 capitalize">
          {field.type} • {field.dataType}
        </div>
      </div>
    </div>
  );
};

// Filter Card Component
const FilterCard: React.FC<{
  filter: ReportFilter;
  selectedFields: ReportField[];
  onUpdate: (updates: Partial<ReportFilter>) => void;
  onDelete: () => void;
}> = ({ filter, selectedFields, onUpdate, onDelete }) => {
  const field = selectedFields.find(f => f.id === filter.fieldId);

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'between', label: 'Between' },
    { value: 'in', label: 'In list' },
    { value: 'not_null', label: 'Not null' }
  ];

  return (
    <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
      <select
        className="px-3 py-2 border border-gray-300 rounded text-sm"
        value={filter.fieldId}
        onChange={(e) => onUpdate({ fieldId: e.target.value })}
      >
        {selectedFields.map(field => (
          <option key={field.id} value={field.id}>{field.label}</option>
        ))}
      </select>
      
      <select
        className="px-3 py-2 border border-gray-300 rounded text-sm"
        value={filter.operator}
        onChange={(e) => onUpdate({ operator: e.target.value as any })}
      >
        {operators.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>
      
      {filter.operator !== 'not_null' && (
        <input
          type={field?.dataType === 'number' ? 'number' : 'text'}
          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
          value={filter.value}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Filter value"
        />
      )}
      
      <button
        onClick={onDelete}
        className="text-red-600 hover:text-red-800"
      >
        <FiTrash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Data Query Builder Component
const DataQueryBuilder: React.FC<{
  report: ReportConfig;
  onUpdateReport: (report: ReportConfig) => void;
}> = ({ report, onUpdateReport }) => {
  const [sqlQuery, setSqlQuery] = useState('');

  useEffect(() => {
    // Generate SQL query based on report configuration
    const generateQuery = () => {
      if (report.fields.length === 0) return '';

      const selectFields = report.fields.map(field => {
        if (field.type === 'measure' && field.aggregation) {
          return `${field.aggregation.toUpperCase()}(${field.table}.${field.name}) as ${field.label}`;
        }
        return `${field.table}.${field.name} as ${field.label}`;
      });

      const fromClause = report.dataSource.table;
      
      const joinClauses = report.dataSource.joins?.map(join => 
        `${join.type.toUpperCase()} JOIN ${join.table} ON ${join.condition}`
      ).join('\n') || '';

      const whereClause = report.filters.length > 0 ? 
        'WHERE ' + report.filters.map(filter => {
          const field = report.fields.find(f => f.id === filter.fieldId);
          if (!field) return '';
          return `${field.table}.${field.name} ${filter.operator} '${filter.value}'`;
        }).join(' AND ') : '';

      const groupByClause = report.fields.some(f => f.type === 'measure') ?
        'GROUP BY ' + report.fields.filter(f => f.type === 'dimension')
          .map(f => `${f.table}.${f.name}`).join(', ') : '';

      return [
        `SELECT ${selectFields.join(',\n       ')}`,
        `FROM ${fromClause}`,
        joinClauses,
        whereClause,
        groupByClause,
        'ORDER BY 1'
      ].filter(Boolean).join('\n');
    };

    setSqlQuery(generateQuery());
  }, [report]);

  return (
    <div className="h-full p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow h-full flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Generated Query</h3>
            <p className="text-sm text-gray-600">
              Auto-generated SQL query based on your report configuration
            </p>
          </div>
          
          <div className="flex-1 p-4">
            <textarea
              className="w-full h-full font-mono text-sm border border-gray-300 rounded-lg p-4 resize-none"
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
              placeholder="SQL query will be generated based on your field selections..."
            />
          </div>
          
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Query will be executed against: <span className="font-medium">{report.dataSource.name}</span>
              </div>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">
                Validate Query
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Preview Component
const ReportPreview: React.FC<{
  report: ReportConfig;
  data: any[];
  chartData: any;
  isLoading: boolean;
}> = ({ report, data, chartData, isLoading }) => {
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <FiRefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Executing report...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <FiBarChart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No data to display. Run the report to see results.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="max-w-6xl mx-auto">
        {report.layout === 'chart' && chartData ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{report.name}</h3>
            <div className="h-96">
              {report.chartConfig?.type === 'line' && <Line data={chartData} options={{ maintainAspectRatio: false }} />}
              {report.chartConfig?.type === 'bar' && <Bar data={chartData} options={{ maintainAspectRatio: false }} />}
              {report.chartConfig?.type === 'pie' && <Pie data={chartData} options={{ maintainAspectRatio: false }} />}
              {report.chartConfig?.type === 'doughnut' && <Doughnut data={chartData} options={{ maintainAspectRatio: false }} />}
              {(!report.chartConfig?.type || report.chartConfig.type === 'bar') && (
                <Bar data={chartData} options={{ maintainAspectRatio: false }} />
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{report.name}</h3>
              <p className="text-sm text-gray-600">{data.length} records</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {report.fields.map(field => (
                      <th
                        key={field.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.slice(0, 100).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {report.fields.map(field => (
                        <td key={field.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row[field.name] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.length > 100 && (
              <div className="px-6 py-3 bg-gray-50 text-sm text-gray-600">
                Showing first 100 of {data.length} records
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportBuilder; 